import React from "react"

import { useBillingSubscription } from "@/billing"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from "@coval/ui"
import { Info, Lock } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"
import { AI_MODELS } from "../utils/aiModelOptions"
import { DEFAULT_MODEL } from "../utils/modelDefaults"
import { ThinkingConfig } from "./ThinkingConfig"

interface LLMModelSelectProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

export const LLMModelSelect: React.FC<LLMModelSelectProps> = ({
  metricRecordData,
  onChange,
}) => {
  const llmModel =
    metricRecordData.metric_metadata?.model_version || DEFAULT_MODEL
  const knowledgeBaseEnabled =
    metricRecordData.metric_metadata?.use_knowledge_base || false

  const { data: subscription } = useBillingSubscription()
  const isEnterprise = subscription?.tier === "enterprise"

  const selectedModel = AI_MODELS.find((model) => model.id === llmModel)

  const handleModelChange = (newModelId: string) => {
    const newModel = AI_MODELS.find((model) => model.id === newModelId)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        model_version: newModelId,
        // Clear thinking when switching to a model that doesn't support it
        model_thinking_enabled: newModel?.supportsThinking
          ? metricRecordData.metric_metadata?.model_thinking_enabled
          : undefined,
      },
    })
  }

  const handleThinkingChange = (enabled: boolean) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        model_thinking_enabled: enabled,
      },
    })
  }

  return (
    <div className={FIELD_CONTAINER_STYLES}>
      <div className={LABEL_ROW_STYLES}>
        <label className={LABEL_STYLES}>Model</label>
        <Tooltip
          content={
            knowledgeBaseEnabled
              ? "Model selection is disabled when Knowledge Base is enabled"
              : "Choose the LLM for metric evaluation"
          }
          side="top"
        >
          <button type="button" className="inline-flex" aria-label="Info">
            <Info className={INFO_ICON_STYLES} />
          </button>
        </Tooltip>
      </div>
      <Select
        value={llmModel}
        onValueChange={handleModelChange}
        disabled={knowledgeBaseEnabled}
      >
        <SelectTrigger aria-label="Model">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {AI_MODELS.map((model) => {
            const isLocked = model.tier === "enterprise" && !isEnterprise
            return (
              <SelectItem key={model.id} value={model.id} disabled={isLocked}>
                <div className="flex items-center justify-between gap-2">
                  <span>{model.label}</span>
                  {isLocked && (
                    <Tooltip content="Enterprise plan required" side="right">
                      <button
                        type="button"
                        className="inline-flex"
                        aria-label="Enterprise plan required"
                      >
                        <Lock className="h-3 w-3 shrink-0 text-coval-text-muted" />
                      </button>
                    </Tooltip>
                  )}
                  {model.tier === "enterprise" && isEnterprise && (
                    <span className="rounded-full bg-coval-warning-surface px-1.5 py-0.5 text-[10px] font-medium text-coval-warning-on-surface">
                      Enterprise
                    </span>
                  )}
                </div>
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      {selectedModel?.tier === "enterprise" && !isEnterprise && (
        <p className="mt-1 text-xs text-coval-warning-text">
          Enterprise plan required for this model.
        </p>
      )}
      {selectedModel?.supportsThinking && !knowledgeBaseEnabled && (
        <ThinkingConfig
          enabled={
            metricRecordData.metric_metadata?.model_thinking_enabled ?? false
          }
          onChange={handleThinkingChange}
        />
      )}
    </div>
  )
}
